import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { type CollectionCase, type CaseNote, type CaseAuditLog } from "@/types/collections";
import { type Transaction } from "@/types/transaction";
import { type ContactUI } from "@/types/ui/customer-detail";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  FileText,
  Activity,
  CreditCard,
  MessageSquare,
  Pencil,
  Save,
  X,
  History,
  ArrowRightLeft,
  Plus,
  Minus,
  Edit,
  UserPlus,
  ArrowLeft,
  Home,
  Inbox,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";
import { TransactionDetailSheet } from "@/components/transactions/transaction-detail-sheet";
import { useUsersList } from "@/hooks/useUsersData";
import { CompleteActionDialog, type CompleteActionData } from "@/components/collections/complete-action-dialog";
import { ConsolidatedActivityTimeline } from "@/components/collections/consolidated-activity-timeline";

// Helper function to generate mock notes history
function mockNotesHistory(caseId: string): CaseNote[] {
  return [
    {
      id: "1",
      caseId: caseId,
      content: "Customer called to discuss payment options. Mentioned they are waiting for a large client payment expected next week. Agreed to follow up on March 28th.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 21, 10, 30),
    },
    {
      id: "2",
      caseId: caseId,
      content: "Sent detailed payment plan proposal via email. Plan includes 3 monthly installments of £5,000 each. Awaiting customer response.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 14, 15),
    },
    {
      id: "3",
      caseId: caseId,
      content: "Initial contact made. Customer acknowledged the outstanding balance and requested time to review their cash flow situation. Scheduled follow-up call for next week.",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 9, 45),
    },
    {
      id: "4",
      caseId: caseId,
      content: "Case assigned to collections team. Customer has been unresponsive to previous payment reminders. Priority set to High due to amount and age of debt.",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];
}

// Helper function to generate mock contacts for a customer
function mockContactsForCustomer(customerId: string): ContactUI[] {
  // Generate different contacts based on customer ID for variety
  const contactSets: Record<string, ContactUI[]> = {
    "1": [
      {
        id: "contact-1-1",
        firstName: "John",
        surname: "Smith",
        email: "john.smith@acmecorp.com",
        phone: "+1 (555) 123-4567",
        jobTitle: "Accounts Payable Manager",
        company: "Acme Corporation",
        customerId: "1",
        address: "123 Business St, New York, NY 10001",
        notes: "Primary contact for all payment-related matters",
        createdAt: new Date(2023, 0, 15).toISOString(),
        lastContact: new Date(2024, 2, 20).toISOString(),
        isPrimary: true,
      },
      {
        id: "contact-1-2",
        firstName: "Sarah",
        surname: "Johnson",
        email: "sarah.johnson@acmecorp.com",
        phone: "+1 (555) 123-4568",
        jobTitle: "Finance Director",
        company: "Acme Corporation",
        customerId: "1",
        address: "123 Business St, New York, NY 10001",
        notes: "Escalation contact for urgent matters",
        createdAt: new Date(2023, 1, 10).toISOString(),
        lastContact: new Date(2024, 2, 15).toISOString(),
        isPrimary: false,
      },
      {
        id: "contact-1-3",
        firstName: "Michael",
        surname: "Chen",
        email: "michael.chen@acmecorp.com",
        phone: "+1 (555) 123-4569",
        jobTitle: "Accounts Payable Clerk",
        company: "Acme Corporation",
        customerId: "1",
        address: "123 Business St, New York, NY 10001",
        notes: "Day-to-day invoice queries",
        createdAt: new Date(2023, 3, 5).toISOString(),
        lastContact: new Date(2024, 2, 18).toISOString(),
        isPrimary: false,
      },
    ],
    "2": [
      {
        id: "contact-2-1",
        firstName: "Emily",
        surname: "Davis",
        email: "emily.davis@techstart.com",
        phone: "+1 (555) 234-5678",
        jobTitle: "CFO",
        company: "TechStart Inc",
        customerId: "2",
        address: "456 Tech Ave, San Francisco, CA 94102",
        notes: "Primary financial decision maker",
        createdAt: new Date(2023, 2, 20).toISOString(),
        lastContact: new Date(2024, 2, 22).toISOString(),
        isPrimary: true,
      },
      {
        id: "contact-2-2",
        firstName: "Robert",
        surname: "Wilson",
        email: "robert.wilson@techstart.com",
        phone: "+1 (555) 234-5679",
        jobTitle: "Accounting Manager",
        company: "TechStart Inc",
        customerId: "2",
        address: "456 Tech Ave, San Francisco, CA 94102",
        notes: "Handles invoice processing",
        createdAt: new Date(2023, 4, 12).toISOString(),
        lastContact: new Date(2024, 2, 19).toISOString(),
        isPrimary: false,
      },
    ],
  };

  // Return contacts for the specific customer, or a default set
  return contactSets[customerId] || [
    {
      id: `contact-${customerId}-1`,
      firstName: "Default",
      surname: "Contact",
      email: `contact@customer${customerId}.com`,
      phone: "+1 (555) 000-0000",
      jobTitle: "Contact Person",
      company: `Customer ${customerId}`,
      customerId: customerId,
      address: "Address not available",
      notes: "Default contact",
      createdAt: new Date(2023, 0, 1).toISOString(),
      lastContact: new Date(2024, 0, 1).toISOString(),
      isPrimary: true,
    },
  ];
}

export default function CaseDetailsPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [caseData, setCaseData] = useState<CollectionCase | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<CollectionCase | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [notesHistory, setNotesHistory] = useState<CaseNote[]>([]);
  const [transactionDetailSheetOpen, setTransactionDetailSheetOpen] = useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState<Transaction | null>(null);
  const [contacts, setContacts] = useState<ContactUI[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [isCompleteActionDialogOpen, setIsCompleteActionDialogOpen] = useState(false);

  // Fetch active users for the Assigned To dropdown
  const { data: users = [] } = useUsersList();
  const activeUsers = users.filter(user => user.status === 'active');

  // Load case data
  useEffect(() => {
    if (caseId) {
      const cases = getGlobalDataStore().getAllCases();
      const foundCase = cases.find((c) => c.caseId === caseId);
      if (foundCase) {
        setCaseData(foundCase);
        // Initialize mock notes history
        setNotesHistory(mockNotesHistory(foundCase.caseId));
        // Load mock contacts for this customer
        setContacts(mockContactsForCustomer(foundCase.customerId));
      } else {
        toast.error("Case not found");
        navigate("/collections/queue");
      }
    }
  }, [caseId, navigate]);

  if (!caseData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">Loading case details...</p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    // Check if there's state from navigation (to preserve filters)
    if (location.state?.from === "queue") {
      navigate(-1);
    } else {
      navigate("/collections/queue");
    }
  };

  const handleEditClick = () => {
    setEditedData({ ...caseData });
    setIsEditMode(true);
    // Initialize selected transactions with all transactions by default
    setSelectedTransactions(new Set(mockTransactions.map(t => t.id.toString())));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());
    setSelectedContactId("");
  };

  const handleContactChange = (contactId: string) => {
    setSelectedContactId(contactId);
    
    if (!editedData) return;
    
    if (contactId === "none") {
      // Clear email and phone when "None" is selected
      setEditedData({
        ...editedData,
        customerEmail: "",
        customerPhone: "",
      });
    } else {
      // Find the selected contact and auto-populate
      const selectedContact = contacts.find((c) => c.id === contactId);
      if (selectedContact) {
        setEditedData({
          ...editedData,
          customerEmail: selectedContact.email,
          customerPhone: selectedContact.phone,
        });
      }
    }
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSaveEdit = () => {
    if (!editedData) return;

    // Validate required fields
    if (!editedData.customerEmail || !editedData.customerPhone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedData.customerEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Get selected transaction details for logging/API call
    const selectedTransactionsList = Array.from(selectedTransactions);
    console.log("Selected transactions:", selectedTransactionsList);
    console.log("Total selected:", selectedTransactionsList.length);

    // Here you would typically make an API call to save the changes
    const message = selectedTransactionsList.length > 0 
      ? `Case updated successfully with ${selectedTransactionsList.length} transaction(s) selected`
      : "Case updated successfully";
    
    toast.success(message);
    setCaseData(editedData);
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());
  };

  const handleCompleteAction = (data: CompleteActionData) => {
    if (!caseData || !caseId) return;

    // Update the case with the completion data
    const updates: Partial<CollectionCase> = {
      notes: data.notes,
    };

    // If a next action was selected, update it
    if (data.nextAction && data.nextActionDate) {
      updates.nextAction = data.nextAction;
      updates.nextActionDate = data.nextActionDate as Date;
    } else {
      // No next action selected - clear it
      updates.nextAction = 'No action scheduled';
      updates.nextActionDate = new Date();
    }

    // If status changed, update it
    if (data.newStatus) {
      updates.status = data.newStatus as 'Active' | 'On Hold' | 'Closed' | 'Promised';
    }

    // Update the case in the global store
    getGlobalDataStore().updateCase(caseId, updates);

    // Add activity entry
    const activityEntry = {
      id: `activity-${Date.now()}`,
      type: 'action_completed' as const,
      description: `Completed action: ${caseData.nextAction}`,
      details: data.outcome,
      timestamp: new Date(),
      user: 'Current User',
    };
    getGlobalDataStore().addActivity(caseId, activityEntry);

    // Add note to history
    const noteEntry: CaseNote = {
      id: `note-${Date.now()}`,
      caseId: caseId,
      content: `Action Completed: ${caseData.nextAction}\n\nOutcome: ${data.outcome}\n\nNotes: ${data.notes}`,
      createdBy: 'Current User',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setNotesHistory([noteEntry, ...notesHistory]);

    // Refresh case data
    const updatedCase = { ...caseData, ...updates };
    setCaseData(updatedCase);
  };

  const currentData = isEditMode && editedData ? editedData : caseData;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Promised":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "Escalated":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  // Mock data for additional details - converted to Transaction type
  const mockTransactions: Transaction[] = [
    {
      id: 1,
      referenceNumber: "INV-2024-001",
      transactionDate: new Date(2024, 0, 15).toISOString(),
      dueDate: new Date(2024, 1, 15).toISOString(),
      customerId: parseInt(caseData.customerId),
      customerName: caseData.customerName,
      transactionType: "invoice",
      amount: caseData.amount * 0.4,
      outstandingAmount: caseData.amount * 0.4,
      currency: "GBP",
      status: "overdue",
      description: "Invoice for services rendered - January 2024",
      createdBy: "System",
      createdAt: new Date(2024, 0, 15).toISOString(),
    },
    {
      id: 2,
      referenceNumber: "INV-2024-002",
      transactionDate: new Date(2024, 1, 1).toISOString(),
      dueDate: new Date(2024, 2, 1).toISOString(),
      customerId: parseInt(caseData.customerId),
      customerName: caseData.customerName,
      transactionType: "invoice",
      amount: caseData.amount * 0.35,
      outstandingAmount: caseData.amount * 0.35,
      currency: "GBP",
      status: "overdue",
      description: "Invoice for services rendered - February 2024",
      createdBy: "System",
      createdAt: new Date(2024, 1, 1).toISOString(),
    },
    {
      id: 3,
      referenceNumber: "INV-2024-003",
      transactionDate: new Date(2024, 2, 10).toISOString(),
      dueDate: new Date(2024, 3, 10).toISOString(),
      customerId: parseInt(caseData.customerId),
      customerName: caseData.customerName,
      transactionType: "invoice",
      amount: caseData.amount * 0.25,
      outstandingAmount: caseData.amount * 0.25,
      currency: "GBP",
      status: "outstanding",
      description: "Invoice for services rendered - March 2024",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10).toISOString(),
    },
  ];

  const mockActivities = [
    {
      id: "1",
      caseId: caseData.caseId,
      type: "call" as const,
      description: "Attempted phone call - no answer",
      outcome: "Left voicemail requesting callback",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "2",
      caseId: caseData.caseId,
      type: "email" as const,
      description: "Sent payment reminder email",
      outcome: "Email delivered successfully",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 9, 15),
    },
    {
      id: "3",
      caseId: caseData.caseId,
      type: "note" as const,
      description: "Customer requested payment plan options",
      outcome: "Sent payment plan proposal",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 11, 45),
    },
    {
      id: "4",
      caseId: caseData.caseId,
      type: "call" as const,
      description: "Spoke with accounts payable manager",
      outcome: "Promised payment by end of week",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 12, 16, 20),
    },
  ];

  const mockCommunications = [
    {
      id: "1",
      caseId: caseData.caseId,
      type: "email" as const,
      direction: "outbound" as const,
      subject: "Payment Reminder - Account Overdue",
      content: "Dear Customer, This is a friendly reminder that your account has an outstanding balance...",
      sentAt: new Date(2024, 2, 18, 9, 15),
      sentBy: "Sarah Johnson",
      status: "delivered" as const,
    },
    {
      id: "2",
      caseId: caseData.caseId,
      type: "email" as const,
      direction: "inbound" as const,
      subject: "RE: Payment Reminder",
      content: "Thank you for your email. We are experiencing temporary cash flow issues...",
      sentAt: new Date(2024, 2, 16, 14, 30),
      sentBy: caseData.customerName,
      status: "delivered" as const,
    },
  ];

  const handleAddNote = async (content: string) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Create new note
    const newNote: CaseNote = {
      id: `note-${Date.now()}`,
      caseId: caseData.caseId,
      content: content,
      createdBy: "Current User",
      createdAt: new Date(),
    };

    // Add note to the beginning of the array (most recent first)
    setNotesHistory([newNote, ...notesHistory]);
  };

  const handleViewTransactionDetail = (transaction: Transaction) => {
    setSelectedTransactionDetail(transaction);
    setTransactionDetailSheetOpen(true);
  };

  const handleViewDocument = (transaction: Transaction) => {
    if (transaction.documentUrl) {
      window.open(transaction.documentUrl, "_blank");
      toast.success("Document opened");
    } else {
      toast.error("No document available for this transaction");
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "call":
        return Phone;
      case "email":
        return Mail;
      case "note":
        return FileText;
      case "payment":
        return CreditCard;
      default:
        return Activity;
    }
  };

  const getAuditLogIcon = (action: CaseAuditLog['action']) => {
    switch (action) {
      case "status_change":
        return ArrowRightLeft;
      case "transaction_added":
        return Plus;
      case "transaction_removed":
        return Minus;
      case "priority_change":
        return AlertTriangle;
      case "assignment_change":
        return UserPlus;
      case "field_update":
        return Edit;
      case "note_added":
        return FileText;
      case "case_created":
        return History;
      default:
        return Activity;
    }
  };

  const getAuditLogColor = (action: CaseAuditLog['action']) => {
    switch (action) {
      case "status_change":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400";
      case "transaction_added":
        return "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400";
      case "transaction_removed":
        return "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400";
      case "priority_change":
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400";
      case "assignment_change":
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
      case "field_update":
        return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400";
      case "note_added":
        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400";
      case "case_created":
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
      default:
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
    }
  };

  // Mock audit log data
  const mockAuditLogs: CaseAuditLog[] = [
    {
      id: "audit-1",
      caseId: caseData.caseId,
      action: "status_change",
      description: "Case status changed",
      fieldName: "status",
      oldValue: "On Hold",
      newValue: "Active",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 22, 9, 15),
    },
    {
      id: "audit-2",
      caseId: caseData.caseId,
      action: "priority_change",
      description: "Priority level updated",
      fieldName: "priority",
      oldValue: "Medium",
      newValue: "High",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "audit-3",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "3",
      transactionNumber: "INV-2024-003",
      transactionAmount: caseData.amount * 0.25,
      transactionDate: new Date(2024, 2, 10),
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 11, 20),
    },
    {
      id: "audit-4",
      caseId: caseData.caseId,
      action: "field_update",
      description: "Customer contact information updated",
      fieldName: "customerEmail",
      oldValue: "old.email@example.com",
      newValue: caseData.customerEmail,
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 16, 45),
    },
    {
      id: "audit-5",
      caseId: caseData.caseId,
      action: "assignment_change",
      description: "Case reassigned to different collector",
      fieldName: "assignedTo",
      oldValue: "John Smith",
      newValue: "Sarah Johnson",
      createdBy: "System",
      createdAt: new Date(2024, 2, 15, 8, 30),
    },
    {
      id: "audit-10",
      caseId: caseData.caseId,
      action: "case_created",
      description: "Collection case created",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];

  // Breadcrumb items
  const breadcrumbItems = [
    { label: "Home", href: "/", icon: Home },
    { label: "Collections", href: "/collections/queue", icon: Inbox },
    { label: "Collections Queue", href: "/collections/queue" },
    { label: `Case ${caseData.caseId}` },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center justify-between">
        <Breadcrumb items={breadcrumbItems} />
        <Button variant="outline" onClick={handleBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Queue
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Case Details</h1>
              <p className="text-muted-foreground mt-1">
                Complete information for case {caseData.caseId}
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant={getPriorityBadgeVariant(currentData.priority)}>
                {currentData.priority} Priority
              </Badge>
              <Badge className={getStatusBadgeColor(currentData.status)}>
                {currentData.status}
              </Badge>
            </div>
          </div>
        </div>
        {!isEditMode ? (
          <Button
            variant="outline"
            size="default"
            onClick={handleEditClick}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" />
            Edit Case
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="default"
              onClick={handleCancelEdit}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              size="default"
              onClick={handleSaveEdit}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Tabs Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="overview" className="space-y-4 mt-0">
            {/* Consolidated Overview - Optimized Layout */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Case Overview
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={getPriorityBadgeVariant(currentData.priority)}>
                      {currentData.priority} Priority
                    </Badge>
                    <Badge className={getStatusBadgeColor(currentData.status)}>
                      {currentData.status}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Primary Information Grid - 4 columns on large screens */}
                <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Case ID */}
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Case ID</p>
                    <p className="text-sm font-semibold">{currentData.caseId}</p>
                  </div>

                  {/* Outstanding Amount */}
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Outstanding Amount
                    </p>
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">
                      £{currentData.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {/* Days in Collection */}
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Days in Collection
                    </p>
                    <p className="text-sm font-semibold">{currentData.daysInCollection} days</p>
                  </div>

                  {/* Risk Level */}
                  <div className="space-y-1">
                    <Label htmlFor="riskLevel" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Risk Level
                    </Label>
                    {isEditMode && editedData ? (
                      <Select
                        value={editedData.riskLevel}
                        onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                          setEditedData({ ...editedData, riskLevel: value })
                        }
                      >
                        <SelectTrigger id="riskLevel" className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getPriorityBadgeVariant(currentData.riskLevel)} className="w-fit">
                        {currentData.riskLevel}
                      </Badge>
                    )}
                  </div>

                  {/* Empty space for alignment */}
                  <div className="hidden lg:block"></div>
                  <div className="hidden lg:block"></div>

                  {/* Case Status - positioned beneath Days in Collection */}
                  <div className="space-y-1">
                    <Label htmlFor="status" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Case Status
                    </Label>
                    {isEditMode && editedData ? (
                      <Select
                        value={editedData.status}
                        onValueChange={(value: 'Active' | 'On Hold' | 'Closed') => 
                          setEditedData({ ...editedData, status: value })
                        }
                      >
                        <SelectTrigger id="status" className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Promised">Promised</SelectItem>
                          <SelectItem value="Escalated">Escalated</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={cn(getStatusBadgeColor(currentData.status), "w-fit")}>
                        {currentData.status}
                      </Badge>
                    )}
                  </div>

                  {/* Priority Level - positioned beneath Risk Level */}
                  <div className="space-y-1">
                    <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Priority Level
                    </Label>
                    {isEditMode && editedData ? (
                      <Select
                        value={editedData.priority}
                        onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                          setEditedData({ ...editedData, priority: value })
                        }
                      >
                        <SelectTrigger id="priority" className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getPriorityBadgeVariant(currentData.priority)} className="w-fit">
                        {currentData.priority}
                      </Badge>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Customer & Contact Information - 3 columns on large screens */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Customer Information
                  </h3>
                  <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                    {/* Customer - Consolidated ID and Name */}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Customer</p>
                      <p className="text-sm font-semibold">{currentData.customerId} {currentData.customerName}</p>
                    </div>

                    {/* Contact Name */}
                    <div className="space-y-1.5">
                      <Label htmlFor="contact" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Name
                      </Label>
                      {isEditMode && editedData ? (
                        <Select
                          value={selectedContactId}
                          onValueChange={handleContactChange}
                        >
                          <SelectTrigger id="contact" className="h-8">
                            <SelectValue placeholder="Select a contact to auto-populate email and phone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {contacts.map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {`${contact.firstName} ${contact.surname}`}
                                {contact.isPrimary && " (Primary)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm">
                          {selectedContactId && selectedContactId !== "none" 
                            ? (() => {
                                const c = contacts.find(c => c.id === selectedContactId);
                                return c ? `${c.firstName} ${c.surname}` : "Not selected";
                              })()
                            : "Not selected"}
                        </p>
                      )}
                    </div>

                    {/* Email Address */}
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email Address
                      </Label>
                      {isEditMode && editedData ? (
                        <Input
                          id="email"
                          type="email"
                          value={editedData.customerEmail}
                          onChange={(e) => setEditedData({ ...editedData, customerEmail: e.target.value })}
                          placeholder="customer@example.com"
                          className="h-8"
                        />
                      ) : (
                        <p className="text-sm">{currentData.customerEmail}</p>
                      )}
                    </div>

                    {/* Phone Number */}
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Phone Number
                      </Label>
                      {isEditMode && editedData ? (
                        <Input
                          id="phone"
                          type="tel"
                          value={editedData.customerPhone}
                          onChange={(e) => setEditedData({ ...editedData, customerPhone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="h-8"
                        />
                      ) : (
                        <p className="text-sm">{currentData.customerPhone}</p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Case Management - 2 columns */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Case Management
                  </h3>
                  <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                    {/* Collection Strategy */}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Collection Strategy</p>
                      <p className="text-sm font-semibold">{currentData.strategy.name}</p>
                      <p className="text-xs text-muted-foreground">{currentData.strategy.type}</p>
                    </div>

                    {/* Assigned User */}
                    <div className="space-y-1">
                      <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                        <UserPlus className="h-3 w-3" />
                        Assigned User
                      </Label>
                      {isEditMode && editedData ? (
                        <Select
                          value={editedData.assignedTo || "unassigned"}
                          onValueChange={(value) => 
                            setEditedData({ ...editedData, assignedTo: value === "unassigned" ? undefined : value })
                          }
                        >
                          <SelectTrigger id="assignedTo" className="h-8">
                            <SelectValue placeholder="Select user..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {activeUsers.map((user) => (
                              <SelectItem key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                {user.firstName} {user.lastName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm">
                          {currentData.assignedTo || "Unassigned"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Next Action - Horizontal layout */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Next Action
                  </h3>
                  {caseData.nextAction && caseData.nextAction !== 'No action scheduled' ? (
                    <div className={cn(
                      "flex items-center justify-between gap-4 p-3 rounded-lg",
                      // Check if action is overdue or due today
                      new Date(caseData.nextActionDate) <= new Date(new Date().setHours(23, 59, 59, 999))
                        ? "bg-amber-50 border border-amber-200"
                        : "bg-muted/30"
                    )}>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{caseData.nextAction}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Due: {format(new Date(caseData.nextActionDate), "MMMM d, yyyy")}
                          {new Date(caseData.nextActionDate) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                            <span className="text-amber-600 font-medium ml-2">(Overdue)</span>
                          )}
                          {new Date(caseData.nextActionDate).toDateString() === new Date().toDateString() && (
                            <span className="text-amber-600 font-medium ml-2">(Due Today)</span>
                          )}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => setIsCompleteActionDialogOpen(true)}
                        className={cn(
                          new Date(caseData.nextActionDate) <= new Date(new Date().setHours(23, 59, 59, 999))
                            ? "bg-amber-600 hover:bg-amber-700"
                            : ""
                        )}
                      >
                        Complete Action
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/30">
                      <p className="text-sm text-muted-foreground">No action scheduled</p>
                    </div>
                  )}
                </div>

                {/* Notes Section - Only show if notes exist or in edit mode */}
                {(currentData.notes || isEditMode) && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Notes
                      </h3>
                      {isEditMode && editedData ? (
                        <Textarea
                          id="notes"
                          value={editedData.notes || ''}
                          onChange={(e) => setEditedData({ ...editedData, notes: e.target.value })}
                          placeholder="Add notes about this case..."
                          rows={3}
                          className="resize-none"
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/30">{currentData.notes}</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions" className="space-y-4 mt-0">
            <Card>
              <CardContent className="pt-6">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isEditMode && <TableHead className="w-12"></TableHead>}
                        <TableHead>Reference Number</TableHead>
                        <TableHead>Transaction Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockTransactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          className={cn(
                            isEditMode && "cursor-pointer hover:bg-muted/50",
                            isEditMode && selectedTransactions.has(transaction.id.toString()) && "bg-muted/30"
                          )}
                          onClick={() => isEditMode && handleToggleTransaction(transaction.id.toString())}
                        >
                          {isEditMode && (
                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedTransactions.has(transaction.id.toString())}
                                onCheckedChange={() => handleToggleTransaction(transaction.id.toString())}
                                aria-label={`Select ${transaction.referenceNumber}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewTransactionDetail(transaction);
                              }}
                              className="text-primary hover:underline transition-colors text-left"
                              title={`View details for ${transaction.referenceNumber}`}
                            >
                              {transaction.referenceNumber}
                            </button>
                          </TableCell>
                          <TableCell>
                            {format(new Date(transaction.transactionDate), "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            {transaction.dueDate ? format(new Date(transaction.dueDate), "dd/MM/yyyy") : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {transaction.transactionType === "invoice" ? "Invoice" : 
                               transaction.transactionType === "credit_note" ? "Credit Note" :
                               transaction.transactionType === "receipt" ? "Receipt" :
                               transaction.transactionType === "debit_adjustment" ? "Debit Adjustment" :
                               transaction.transactionType === "credit_adjustment" ? "Credit Adjustment" :
                               transaction.transactionType}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            £{transaction.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            £{(transaction.outstandingAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.status === "overdue"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {transaction.status === "overdue" ? "Overdue" :
                               transaction.status === "outstanding" ? "Outstanding" :
                               transaction.status === "completed" ? "Completed" :
                               transaction.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Outstanding:</span>
                  <span className="text-xl font-bold text-red-600 dark:text-red-400">
                    £{caseData.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4 mt-0">
            <ConsolidatedActivityTimeline
              activities={mockActivities}
              communications={mockCommunications}
              notes={notesHistory}
              isEditMode={isEditMode}
              onAddNote={handleAddNote}
            />
          </TabsContent>

          <TabsContent value="audit" className="space-y-4 mt-0">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Audit Log
                </CardTitle>
                <CardDescription>
                  Complete chronological history of all case modifications and changes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockAuditLogs.map((log, index) => {
                    const Icon = getAuditLogIcon(log.action);
                    const colorClass = getAuditLogColor(log.action);
                    
                    return (
                      <div key={log.id} className="relative">
                        {index !== mockAuditLogs.length - 1 && (
                          <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                        )}
                        <div className="flex gap-4">
                          <div className={cn(
                            "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
                            colorClass
                          )}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-1">
                                <p className="font-medium text-sm">{log.description}</p>
                                
                                {/* Status/Priority/Field Changes */}
                                {(log.action === "status_change" || log.action === "priority_change" || log.action === "field_update" || log.action === "assignment_change") && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Badge variant="outline" className="text-xs">
                                      {log.oldValue || "None"}
                                    </Badge>
                                    <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                    <Badge variant="outline" className="text-xs">
                                      {log.newValue}
                                    </Badge>
                                  </div>
                                )}
                                
                                {/* Transaction Added */}
                                {log.action === "transaction_added" && log.transactionNumber && (
                                  <div className="text-sm text-muted-foreground space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{log.transactionNumber}</span>
                                      <span>•</span>
                                      <span className="font-medium text-green-600 dark:text-green-400">
                                        £{log.transactionAmount?.toLocaleString()}
                                      </span>
                                    </div>
                                    {log.transactionDate && (
                                      <div className="text-xs">
                                        Invoice Date: {format(log.transactionDate, "MMM d, yyyy")}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground whitespace-nowrap">
                                {format(log.createdAt, "MMM d, yyyy h:mm a")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                <User className="h-3 w-3 mr-1" />
                                {log.createdBy}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {mockAuditLogs.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit log entries found for this case.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>

      {/* Transaction Detail Sheet */}
      <TransactionDetailSheet
        open={transactionDetailSheetOpen}
        onOpenChange={setTransactionDetailSheetOpen}
        transaction={selectedTransactionDetail}
        onViewDocument={handleViewDocument}
      />

      <CompleteActionDialog
        open={isCompleteActionDialogOpen}
        onOpenChange={setIsCompleteActionDialogOpen}
        currentAction={caseData ? {
          action: caseData.nextAction,
          dueDate: typeof caseData.nextActionDate === 'string' 
            ? caseData.nextActionDate 
            : format(caseData.nextActionDate, 'yyyy-MM-dd'),
        } : null}
        caseId={caseId || ''}
        onComplete={handleCompleteAction}
      />
    </div>
  );
}
